/**
 * The Notion import's routes (CS r4a deliverable 103; DECISIONS 176 to 178), owner 2.B-1 by
 * stage, since `exit-2a`, frozen. An export arrives in parts of `IMPORT_PART_BYTES` (resumable),
 * is planned by the `import.plan` job, mapped and reviewed page by page, dry-run, applied by the
 * `import.apply` job and undone by `import.undo`. A step the run's status refuses answers 409
 * `import_state` with `details.status`; every write that takes `baseRv` declares 409
 * `row_conflict`. Only `import.pages.resolve` names a document (its target, an owner pointer, so a
 * reference document is refused before the handler): the importer never writes into one.
 */
import { IdParams } from '../dto/common.js';
import { IMPORT_PART_BYTES, IMPORT_PART_MIME_TYPES, ImportApplyV1, ImportDryRunV1, ImportMappingPutV1, ImportPageDetailV1, ImportPageParams, ImportPagesQueryV1, ImportPagesResolveResultV1, ImportPagesResolveV1, ImportPageV1, ImportPartFieldsV1, ImportPartParams, ImportPartResultV1, ImportRunCompleteV1, ImportRunCreateV1, ImportRunV1, } from '../dto/import.js';
import { listOf } from '../dto/list.js';
import { ImportPlanOutlineV1 } from '../import/plan.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_IMPORT_BYTES, EXAMPLE_IMPORT_DRY_RUN, EXAMPLE_IMPORT_MAPPING_SAVED, EXAMPLE_IMPORT_PAGE, EXAMPLE_IMPORT_PAGE_DETAIL, EXAMPLE_IMPORT_PART, EXAMPLE_IMPORT_PLAN_OUTLINE, EXAMPLE_IMPORT_RUN_APPLIED, EXAMPLE_IMPORT_RUN_APPLYING, EXAMPLE_IMPORT_RUN_CANCELLED, EXAMPLE_IMPORT_RUN_CREATED, EXAMPLE_IMPORT_RUN_MAPPED, EXAMPLE_IMPORT_RUN_PLANNED, EXAMPLE_IMPORT_RUN_PLANNING, EXAMPLE_IMPORT_RUN_UNDOING, EXAMPLE_NOTION_IDS, } from './examples-import.js';
const ids = EXAMPLE_IDS;
const planned = { id: ids.importRunPlanned.id };
const uploading = { id: ids.importRunUploading.id };
const imports = { auth: 'session', status: 'frozen', since: 'exit-2a', owner: '2.B-1' };
const STATE = { 409: ['import_state'] };
const STATE_INVALID = { 409: ['import_state'], 422: ['invalid_body'] };
/** Start a run for a file of this size; its parts follow (2.B-1 s1). */
export const importRunsCreate = defineRoute({
    id: 'import.runs.create',
    method: 'POST',
    path: '/api/import/runs',
    body: ImportRunCreateV1,
    response: { kind: 'json', schema: ImportRunV1, status: 201 },
    errors: { 422: ['invalid_body'] },
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: { body: { fileName: 'drowned-lanterns-workspace.zip', bytes: EXAMPLE_IMPORT_BYTES } },
        response: EXAMPLE_IMPORT_RUN_CREATED,
    },
});
/**
 * Part `n` (from 0) of the upload, multipart in `part`: every part `IMPORT_PART_BYTES` but the
 * last. A part out of order answers 409 `import_state`; a re-sent part is accepted when its bytes
 * equal those stored. Resume reads `partsReceived` from `import.runs.get`.
 */
export const importRunsUploadPart = defineRoute({
    id: 'import.runs.uploadPart',
    method: 'PUT',
    path: '/api/import/runs/:id/parts/:n',
    params: ImportPartParams,
    requestKind: 'multipart',
    upload: { field: 'part', maxBytes: IMPORT_PART_BYTES, mimeTypes: IMPORT_PART_MIME_TYPES },
    body: ImportPartFieldsV1,
    response: { kind: 'json', schema: ImportPartResultV1 },
    errors: STATE_INVALID,
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: { params: { ...uploading, n: 1 }, body: {}, file: EXAMPLE_IMPORT_PART },
        response: { partsReceived: 2, bytesReceived: 16 * 1024 * 1024 },
        consumes: true,
        success: {
            skip: "a part is stored only at its full 8 MiB length and into the upload's file under the data directory, neither of which an example or a database seed holds",
        },
    },
});
/** Every part is in: check the SHA-256, store the export and queue `import.plan`. */
export const importRunsComplete = defineRoute({
    id: 'import.runs.complete',
    method: 'POST',
    path: '/api/import/runs/:id/complete',
    params: IdParams,
    body: ImportRunCompleteV1,
    response: { kind: 'json', schema: ImportRunV1 },
    errors: { 409: ['import_state'], 422: ['upload_incomplete'] },
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: {
            params: uploading,
            body: { sha256: '9c4e2a7f1b3d5e6f80a2c4e6081a3c5e7f9b1d3f5a7c9e1b3d5f7a9c1e3b5d7f' },
        },
        response: EXAMPLE_IMPORT_RUN_PLANNING,
        consumes: true,
        success: {
            skip: "completing needs every part in the upload's file under the data directory and its SHA-256, which a database seed does not hold",
        },
    },
});
/** The runs, newest first, at most 50 (Import history). */
export const importRunsList = defineRoute({
    id: 'import.runs.list',
    method: 'GET',
    path: '/api/import/runs',
    response: { kind: 'json', schema: listOf(ImportRunV1) },
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        response: { items: [EXAMPLE_IMPORT_RUN_PLANNED, EXAMPLE_IMPORT_RUN_APPLIED], next: null },
    },
});
/** One run. */
export const importRunsGet = defineRoute({
    id: 'import.runs.get',
    method: 'GET',
    path: '/api/import/runs/:id',
    params: IdParams,
    response: { kind: 'json', schema: ImportRunV1 },
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: { params: planned },
        response: EXAMPLE_IMPORT_RUN_PLANNED,
    },
});
/** The stored plan without its pages, for the mapping step (DECISIONS 178); 409 before `planned`. */
export const importRunsPlan = defineRoute({
    id: 'import.runs.plan',
    method: 'GET',
    path: '/api/import/runs/:id/plan',
    params: IdParams,
    response: { kind: 'json', schema: ImportPlanOutlineV1 },
    errors: STATE,
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: { params: planned },
        response: EXAMPLE_IMPORT_PLAN_OUTLINE,
        success: {
            skip: 'serving the plan reads its stored file under the data directory, which a database seed does not hold',
        },
    },
});
/**
 * Cancel a run that is uploading, planning or planned. An `applying` run records the request
 * (`cancelRequestedAt`) and stays `applying` until its job stops; any other status answers 409.
 */
export const importRunsCancel = defineRoute({
    id: 'import.runs.cancel',
    method: 'POST',
    path: '/api/import/runs/:id/cancel',
    params: IdParams,
    response: { kind: 'json', schema: ImportRunV1 },
    errors: STATE,
    reference: 'none',
    ...imports,
    stage: 's1',
    examples: {
        request: { params: uploading },
        response: EXAMPLE_IMPORT_RUN_CANCELLED,
        consumes: true,
    },
});
/** Save the GM's mapping of databases, properties and status options (2.B-1 s2). */
export const importRunsPutMapping = defineRoute({
    id: 'import.runs.putMapping',
    method: 'PUT',
    path: '/api/import/runs/:id/mapping',
    params: IdParams,
    body: ImportMappingPutV1,
    response: { kind: 'json', schema: ImportRunV1 },
    errors: { 409: ['row_conflict', 'import_state'], 422: ['invalid_body'] },
    reference: 'none',
    ...imports,
    stage: 's2',
    examples: {
        request: {
            params: planned,
            body: { baseRv: 2, mapping: EXAMPLE_IMPORT_MAPPING_SAVED },
        },
        response: EXAMPLE_IMPORT_RUN_MAPPED,
        consumes: true,
        success: {
            skip: 'checking a mapping reads the stored plan from its file under the data directory, which a database seed does not hold',
        },
    },
});
/** What an apply would do now, writing no document (the dry-run report). */
export const importRunsDryRun = defineRoute({
    id: 'import.runs.dryRun',
    method: 'POST',
    path: '/api/import/runs/:id/dry-run',
    params: IdParams,
    response: { kind: 'json', schema: ImportDryRunV1 },
    errors: STATE,
    reference: 'none',
    ...imports,
    stage: 's2',
    examples: {
        request: { params: planned },
        response: EXAMPLE_IMPORT_DRY_RUN,
        success: {
            skip: 'a dry run reads the stored plan from its file under the data directory, which a database seed does not hold',
        },
    },
});
/** The match review, paged and filtered (tier, choice, re-import action, type, skipped before). */
export const importPagesList = defineRoute({
    id: 'import.pages.list',
    method: 'GET',
    path: '/api/import/runs/:id/pages',
    params: IdParams,
    query: ImportPagesQueryV1,
    response: { kind: 'json', schema: listOf(ImportPageV1) },
    reference: 'none',
    ...imports,
    stage: 's2',
    examples: {
        request: { params: planned, query: { tier: 'stub', limit: 50 } },
        response: { items: [EXAMPLE_IMPORT_PAGE], next: null },
    },
});
/**
 * One page with its body, as 2.A's `readPage` gives it, for Compare. Its `srdDocumentId` may name
 * a reference document, read and never written.
 */
export const importPagesGet = defineRoute({
    id: 'import.pages.get',
    method: 'GET',
    path: '/api/import/runs/:id/pages/:notionId',
    params: ImportPageParams,
    response: { kind: 'json', schema: ImportPageDetailV1 },
    reference: 'read',
    ...imports,
    stage: 's2',
    examples: {
        request: { params: { ...planned, notionId: EXAMPLE_NOTION_IDS.rell } },
        response: EXAMPLE_IMPORT_PAGE_DETAIL,
        success: {
            skip: "a page's body is read from the stored plan and the uploaded export, files under the data directory that a database seed does not hold",
        },
    },
});
/**
 * One choice for the pages named, or for every page the filter selects ("Apply to selected"),
 * with the target of a Fill or a Pair. A choice the tier does not offer, or any choice for a page
 * gone from the export, answers 422 `invalid_body`.
 */
export const importPagesResolve = defineRoute({
    id: 'import.pages.resolve',
    method: 'POST',
    path: '/api/import/runs/:id/pages/resolve',
    params: IdParams,
    body: ImportPagesResolveV1,
    response: { kind: 'json', schema: ImportPagesResolveResultV1 },
    errors: STATE_INVALID,
    reference: 'deny',
    docIds: { owner: ['body.targetDocId'] },
    ...imports,
    stage: 's2',
    examples: {
        request: {
            params: planned,
            body: {
                notionIds: [EXAMPLE_NOTION_IDS.rell],
                choice: 'fill',
                targetDocId: ids.npcGuardCaptainRell.id,
            },
        },
        response: { updated: 1 },
        consumes: true,
    },
});
/** Apply the run's choices: `planned` to `applying`, the `import.apply` job queued (2.B-1 s3). */
export const importRunsApply = defineRoute({
    id: 'import.runs.apply',
    method: 'POST',
    path: '/api/import/runs/:id/apply',
    params: IdParams,
    body: ImportApplyV1,
    response: { kind: 'json', schema: ImportRunV1 },
    errors: { 409: ['row_conflict', 'import_state'] },
    reference: 'none',
    ...imports,
    stage: 's3',
    examples: {
        request: { params: planned, body: { baseRv: 2 } },
        response: EXAMPLE_IMPORT_RUN_APPLYING,
        consumes: true,
        success: {
            skip: 'applying re-checks the mapping against the stored plan, read from its file under the data directory, which a database seed does not hold',
        },
    },
});
/** Undo the newest applied run: `applied` to `undoing`, the `import.undo` job queued (2.B-1 s4). */
export const importRunsUndo = defineRoute({
    id: 'import.runs.undo',
    method: 'POST',
    path: '/api/import/runs/:id/undo',
    params: IdParams,
    response: { kind: 'json', schema: ImportRunV1 },
    errors: STATE,
    reference: 'none',
    ...imports,
    stage: 's4',
    examples: {
        request: { params: { id: ids.importRunApplied.id } },
        response: EXAMPLE_IMPORT_RUN_UNDOING,
        consumes: true,
    },
});
//# sourceMappingURL=import.js.map